import { useEffect, useCallback, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { ApiUrl } from "./ApiUrl";
import { useLanguage } from "./languageContext";
import translations from "./translation.json";
import Select from "react-select";
import { internalUsers, rolesThatCanCreateUsers } from "./constants/userTypes";
const Users = ({
  setDisplayComponent,
  setIsLoading,
  isDarkMode,
  usersData,
  setusers,
  total,
  setTotal,
  deleteStates,
  setDeleteStates,
  selectedDealershipOption,
}) => {
  const token = localStorage.getItem("token");
  const userData = JSON.parse(localStorage.getItem("userData"));
  const userType = localStorage.getItem("userType");
  const canCreateUsers = userData?.roles?.some((role) =>
    rolesThatCanCreateUsers.includes(role.name)
  );
  const canSendEmails = userData?.roles?.some((role) =>
    ["Super Admin", "Admin"].includes(role.name)
  );
  const [typeuser, settypeuser] = useState(
    ["Animator", "Representative", "Manager", "Employee"].includes(userType) ? "2" : "1"
  );
  const [key, setKey] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [dataEnd, setDataEnd] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const { language } = useLanguage();
  const containerRef = useRef(null);
  const requestVersion = useRef(0);
  const fetching = useRef(false);
  const nextPage = useRef(1);
  const loadedFilters = useRef("");
  const currentFilterSnapshot = useRef("");
  const dealershipIds = JSON.stringify(
    (selectedDealershipOption || []).map((option) => option.value)
  );
  const effectiveType = dealershipIds !== "[]" ? "2" : typeuser;
  const filters = useMemo(() => ({
    userType: effectiveType,
    search_parameter: appliedSearch,
    dealership_ids: JSON.parse(dealershipIds),
  }), [effectiveType, appliedSearch, dealershipIds]);
  const filterKey = JSON.stringify(filters);
  currentFilterSnapshot.current = JSON.stringify({ ...filters, search_input: key });
  const activeSearch = key.length > 0;
  const filtersReady = hasLoaded && loadedFilters.current === filterKey && key === appliedSearch;

  useEffect(() => {
    const timer = setTimeout(() => setAppliedSearch(key), 400);
    return () => clearTimeout(timer);
  }, [key]);

  const fetchUsers = useCallback(async (page = 1, signal) => {
    const version = ++requestVersion.current;
    fetching.current = true;
    setIsFetching(true);
    if (page === 1) setHasLoaded(false);
    try {
      const endpoint = filters.search_parameter ? "search-users" : "get-all-users";
      const response = await fetch(ApiUrl + "/api/user/" + endpoint + "?page=" + page, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(filters),
        signal,
      });
      const data = await response.json();
      if (signal?.aborted || version !== requestVersion.current) return;
      if (!response.ok || data.status !== "success") throw new Error(data.message);
      const result = data.event_list || data.list_of_users;
      setusers((previous) => page === 1 ? result.data : [...previous, ...result.data]);
      setDeleteStates((previous) => page === 1
        ? result.data.map(() => false)
        : [...(previous || []), ...result.data.map(() => false)]);
      setTotal(result.total);
      nextPage.current = page + 1;
      setDataEnd(page >= result.last_page);
      loadedFilters.current = JSON.stringify(filters);
      setHasLoaded(true);
    } catch (error) {
      if (error.name !== "AbortError" && version === requestVersion.current) {
        toast.error(error.message || translations.alertMessages.wentWrong[language]);
      }
    } finally {
      if (version === requestVersion.current) {
        fetching.current = false;
        setIsFetching(false);
      }
    }
  }, [filters, token, language, setusers, setDeleteStates, setTotal]);

  useEffect(() => {
    const controller = new AbortController();
    setusers([]);
    setTotal(0);
    setDataEnd(false);
    fetchUsers(1, controller.signal);
    return () => {
      controller.abort();
      requestVersion.current += 1;
    };
  }, [fetchUsers, setusers, setTotal]);

  const handleScroll = () => {
    const container = containerRef.current;
    if (container && filtersReady && !fetching.current && !dataEnd &&
        container.scrollHeight - container.scrollTop - container.clientHeight < 300) {
      fetchUsers(nextPage.current);
    }
  };

  const handleDelete = async (id) => {
    setIsLoading(true);
    try {
      const response = await fetch(ApiUrl + "/api/user/delete-users", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id, ...filters }),
      });
      const data = await response.json();
      if (!response.ok || data.status !== "success") throw new Error(data.message);
      toast.success(translations.alertMessages.userDeletion[language]);
      await fetchUsers(1);
    } catch (error) {
      toast.error(error.message || translations.alertMessages.wentWrong[language]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async (email) => {
    if (!canSendEmails) return;
    if (!window.confirm(translations.usersPage.passwordReset.resendConfirm[language]
        .replace("{email}", email))) return;
    setIsLoading(true);
    try {
      const response = await fetch(ApiUrl + "/api/user/send-set-password-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (!response.ok || data.status !== "success") throw new Error(data.message);
      toast.success(translations.alertMessages.emailSentSuccess[language]);
    } catch (error) {
      toast.error(error.message || translations.alertMessages.wentWrong[language]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkReset = async () => {
    if (!canSendEmails || isSendingReset || !filtersReady) return;
    setIsSendingReset(true);
    const snapshot = currentFilterSnapshot.current;
    // Capture the current filters for both the preview and the confirmed send.
    const sendRequest = async (preview) => {
      const response = await fetch(ApiUrl + "/api/user/send-filtered-password-reset-emails", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...filters, preview }),
      });
      const data = await response.json();
      if (!response.ok || data.status !== "success") throw new Error(data.message);
      return data;
    };
    try {
      const preview = await sendRequest(true);
      if (currentFilterSnapshot.current !== snapshot) {
        toast.info(translations.usersPage.passwordReset.filtersChanged[language]);
        return;
      }
      if (!preview.count) {
        toast.info(translations.usersPage.passwordReset.noRecipients[language]);
        return;
      }
      if (!window.confirm(translations.usersPage.passwordReset.confirm[language]
          .replace("{count}", preview.count))) return;
      const result = await sendRequest(false);
      toast.success(translations.usersPage.passwordReset.queued[language]
        .replace("{count}", result.count));
    } catch (error) {
      toast.error(error.message || translations.alertMessages.wentWrong[language]);
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleClearFilters = () => {
    setKey("");
    setAppliedSearch("");
  };

  return (
    <div className="Events">
      <div className="container maincont">
        <div id="app" className="appcountainer2">
          <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between py-2">
            <div>
              <h2 id="h1" className={`${isDarkMode ? "darkthead" : ""}`}>
                {translations.usersPage.title[language]}
              </h2>
            </div>

            <div className="d-flex flex-wrap align-items-center gap-2">
              {canCreateUsers && (
                  <div className="d-grid gap-2">
                    {/* <Link to='/create-user' style={{ textDecoration: "none" }}> */}
                    <button
                      type="button"
                      className="d-flex btn btn-primary align-items-center admin-btn gap-1"
                      // id="create-user-link"
                      // fdprocessedid="dyv9n"
                      // data-bs-dismiss="offcanvas"
                      onClick={() => {
                        setDisplayComponent((prev) => {
                          // setPrevDisplay(prev);
                          return "create-user";
                        });
                        window.history.pushState(
                          { prevState: "prev" },
                          "title",
                          "/event-portal?page=create-user"
                        );
                      }}
                    >
                      <i className="bi bi-person-add"></i>{" "}
                      {translations.topAndsidebar.createUser[language]}
                    </button>
                    {/* </Link> */}
                  </div>
                )}
              {canSendEmails && (
                <button
                  type="button"
                  className="d-flex btn btn-primary align-items-center admin-btn gap-1"
                  disabled={isSendingReset || !filtersReady || total === 0}
                  onClick={handleBulkReset}
                >
                  <i className="bi bi-envelope-arrow-up"></i>
                  {translations.usersPage.passwordReset[isSendingReset ? "sending" : "button"][language]}
                </button>
              )}
              {userType &&
                internalUsers.includes(userType) &&
                userType !== "Animator" && (
                  <div
                    className=""
                    style={{ width: language === "fr" ? "210px" : "180px" }}
                  >
                    <Select
                      className="w-100 selectpicker"
                      isDisabled={dealershipIds !== "[]"}
                      aria-labelledby="dealership-name-quick"
                      // isClearable
                      options={[
                        {
                          label:
                            translations.commonWords.internalUsers[language],
                          value: "1",
                        },
                        {
                          label:
                            translations.commonWords.externalUsers[language],
                          value: "2",
                        },
                      ]}
                      placeholder={translations["dropdown"].select[language]}
                      isSearchable={false}
                      value={
                        [
                          {
                            label:
                              translations.commonWords.internalUsers[language],
                            value: "1",
                          },
                          {
                            label:
                              translations.commonWords.externalUsers[language],
                            value: "2",
                          },
                        ].find((option) => option.value === effectiveType) || {
                          value: "",
                          label: translations["dropdown"].select[language],
                        }
                      }
                      onChange={(selectedOption) => {
                        settypeuser(selectedOption.value);
                      }}
                      // maxMenuHeight={"120px"}
                      styles={{
                        input: (provided) => ({
                          ...provided,
                          color: isDarkMode ? "#ffffff" : "black", // Color of the text when typing
                        }),
                        control: (provided, state) => ({
                          ...provided,
                          backgroundColor: isDarkMode ? "transparent" : "white",
                          border: state.isFocused
                            ? "1px solid #80bdff"
                            : isDarkMode
                            ? "1px solid #495057"
                            : "1px solid #dee2e6", // Adjust the border color for focus
                          boxShadow: state.isFocused
                            ? "0 0 0 0.2rem rgba(0, 123, 255, 0.25)"
                            : "none", // Bootstrap's box shadow for focus
                          borderRadius: ".45rem",
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          padding: "0.18rem 0.55rem", // Bootstrap's padding for the value container
                        }),
                        indicatorSeparator: (provided) => ({
                          ...provided,
                          display: "none", // Hide the indicator separator
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          padding: "0.375rem 0.75rem", // Bootstrap's padding for the dropdown indicator
                          color: isDarkMode ? "#ffffff" : "#868686",
                        }),
                        menu: (provided) => ({
                          ...provided,
                          color: isDarkMode ? "#ffffff" : "black",
                          backgroundColor: isDarkMode ? "#1f2023" : "white",
                          margin: "0", // Remove default margin
                          zIndex: "999",
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: isDarkMode ? "#ffffff" : "#333333", // Specify your desired color for the placeholder
                          // Additional styles for placeholder
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: isDarkMode ? "#ffffff" : "",
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          color: isDarkMode ? "#ffffff" : "black",
                          backgroundColor: state.isHovered
                            ? "#80bdff"
                            : isDarkMode
                            ? "#1f2023"
                            : "white",
                          ":hover": {
                            backgroundColor: isDarkMode ? "#34DBB4" : "#34DBB4",
                          },
                        }),
                      }}
                    />
                  </div>
                )}
            </div>
          </div>
          <div className="headersec">
            <div className="d-flex mb-2">
              <input
                type="text"
                style={{
                  borderColor: "#dee2e6",
                  backgroundColor: isDarkMode ? "transparent" : "",
                  color: isDarkMode ? "#ffffff" : "#000000",
                }}
                autoComplete="off"
                className={`form-control ${isDarkMode ? "dark-mode" : ""}`}
                id="searchInput"
                placeholder={
                  translations.commonFields.searchbar.search[language]
                }
                value={key}
                onChange={(e) => {
                  setKey(e.target.value);
                }}
              />
              {activeSearch && (
                <button
                  className="btn bi bi-x-lg searchBtn"
                  style={{ padding: "6px 8px" }}
                  onClick={handleClearFilters}
                ></button>
              )}
              <button
                className="btn bi bi-search searchBtn"
                style={{ padding: "6px 8px" }}
                onClick={() => {
                  setAppliedSearch(key);
                  if (key === appliedSearch) fetchUsers(1);
                }}
              ></button>
            </div>
          </div>
          <div
            // className="bodysecsearch"
            role="region"
            aria-label={translations.usersPage.title[language]}
            aria-busy={isFetching}
            ref={containerRef}
            onScroll={handleScroll}
            style={{
              display: "flex",
              flexDirection: "column",
              overflowX: "auto",
              maxHeight: "calc(100vh - 255px)",
            }}
          >
            <table className="table table-hover align-middle text-center">
              <thead style={{ position: "sticky", top: "0", zIndex: "1" }}>
                <tr>
                  <th
                    scope="col"
                    className={`${isDarkMode ? "darkthead" : ""}`}
                  >
                    #
                  </th>
                  <th
                    scope="col"
                    className={`${isDarkMode ? "darkthead" : ""}`}
                  >
                    {translations.commonFields.name[language]}
                  </th>
                  <th
                    scope="col"
                    className={`${isDarkMode ? "darkthead" : ""}`}
                  >
                    {translations.commonFields.email[language]}
                  </th>
                  <th
                    scope="col"
                    className={`${isDarkMode ? "darkthead" : ""}`}
                  >
                    {translations.usersPage.createUser.userType[language]}
                  </th>
                  <th
                    scope="col"
                    className={`${isDarkMode ? "darkthead" : ""}`}
                  >
                    {translations.usersPage.createUser.status[language]}
                  </th>
                  {/* <th scope="col"></th> */}
                  {(canSendEmails || (userData &&
                    userData.roles[0].permissions.some(
                      (item) => item.name === "Edit User"
                    )) ||
                    (userData &&
                      userData.roles[0].permissions.some(
                        (item) => item.name === "Delete User"
                      ))) && (
                    <th
                      scope="col"
                      colSpan={
                        Number(Boolean(canSendEmails)) +
                        Number(Boolean(userData?.roles?.[0]?.permissions?.some((item) => item.name === "Edit User"))) +
                        Number(Boolean(userData?.roles?.[0]?.permissions?.some((item) => item.name === "Delete User")))
                      }
                      // colSpan={internalUsers.includes(userType) ? 3 : 1}
                      className={`${isDarkMode ? "darkthead" : ""}`}
                    >
                      Actions
                    </th>
                  )}
                  {/* <th scope="col"></th> */}
                </tr>
              </thead>
              <tbody id="searchResults">
                {usersData &&
                  usersData.map((user, index) => {
                    return (
                      <tr className="result-box" key={user.id}>
                        <th className="custID" scope="row">
                          {user.id}
                        </th>
                        <td className="dlrname">{user.name}</td>
                        <td className="salesrep">{user.email}</td>
                        <td className="semainesearch">
                          {user.user_type === "1"
                            ? `${
                                translations.commonWords.internalUsers[language]
                              } (${user.roles[0]?.name || " "})`
                            : `${
                                translations.commonWords.externalUsers[language]
                              } (${user.roles[0]?.name || " "})`}
                        </td>
                        <td
                          className="semainesearch"
                          id={
                            user.account_activate === "Yes"
                              ? "statusActive"
                              : "statusInactive"
                          }
                        >
                          {user.account_activate === "Yes"
                            ? "Active"
                            : "Inactive"}
                        </td>
                        {canSendEmails && <td>
                          {
                            user?.roles[0]?.name !== "Employee" && (
                              <button
                                type="button"
                                className="btn btn-light copy-button"
                                data-customer-id="405"
                                title={translations.commonFields.resendEmailHelp[language]}
                                onClick={() => handleResend(user.email)}
                              >
                                <i className="bi bi-send"></i>{" "}
                                {
                                  translations.commonFields.resendEmail[
                                    language
                                  ]
                                }
                              </button>
                            )}
                        </td>}
                        {((userData &&
                          userData.roles[0].permissions.some(
                            (item) => item.name === "Edit User"
                          )) ||
                          (userData &&
                            userData.roles[0].permissions.some(
                              (item) => item.name === "Delete User"
                            ))) && (
                          <>
                            {userData &&
                              userData.roles[0].permissions.some(
                                (item) => item.name === "Edit User"
                              ) && (
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-light edit-button"
                                    data-customer-id="405"
                                    onClick={() => {
                                      setDisplayComponent("edit-event");

                                      window.history.pushState(
                                        {
                                          id: "gallery",
                                          randomData: window.Math.random(),
                                        },
                                        "title",
                                        "/event-portal?page=edit-user&user_id=" +
                                          user.id
                                      );
                                    }}
                                  >
                                    <i className="bi bi-pencil me-1"></i>
                                    {
                                      translations.commonFields.editBtn[
                                        language
                                      ]
                                    }
                                  </button>
                                </td>
                              )}
                            {userData &&
                              userData.roles[0].permissions.some(
                                (item) => item.name === "Delete User"
                              ) && (
                                <td style={{ width: "170px" }}>
                                  <div className="btn-group" role="group">
                                    {deleteStates && deleteStates[index] ? (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-success delete-button"
                                          data-customer-id="405"
                                          onClick={() =>
                                            handleDelete(user.id, index)
                                          }
                                        >
                                          {
                                            translations.commonFields.confirm[
                                              language
                                            ]
                                          }
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-secondary cancel-button"
                                          onClick={() =>
                                            setDeleteStates((prevStates) => {
                                              const newStates = [...prevStates];
                                              newStates[index] = false;
                                              return newStates;
                                            })
                                          }
                                        >
                                          {
                                            translations.commonFields.cancel[
                                              language
                                            ]
                                          }
                                        </button>
                                      </>
                                    ) : (
                                      <button
                                        type="button"
                                        className="btn btn-danger before-delete-button"
                                        dataset-button-state="delete"
                                        onClick={() => {
                                          setDeleteStates((prevStates) => {
                                            const newStates = [...prevStates];
                                            newStates[index] = true;
                                            return newStates;
                                          });
                                        }}
                                        disabled={user.roles[0]?.name === "Super Admin" && userType !== "Super Admin" }
                                      >
                                        <i className="bi bi-trash"></i>{" "}
                                        {
                                          translations.commonFields.delete[
                                            language
                                          ]
                                        }
                                      </button>
                                    )}
                                  </div>
                                </td>
                              )}
                          </>
                        )}
                      </tr>
                    );
                  })}
                {isFetching && (
                  <tr className="skeleton-loader">
                    <th className="p-2">
                      <div
                        className={isDarkMode ? "skeleton-dark" : "skeleton"}
                      ></div>
                    </th>
                    <td className="p-2">
                      <div
                        className={isDarkMode ? "skeleton-dark" : "skeleton"}
                      ></div>
                    </td>
                    <td className="p-2">
                      <div
                        className={isDarkMode ? "skeleton-dark" : "skeleton"}
                      ></div>
                    </td>
                    <td className="p-2">
                      <div
                        className={isDarkMode ? "skeleton-dark" : "skeleton"}
                      ></div>
                    </td>
                    <td className="p-2">
                      <div
                        className={isDarkMode ? "skeleton-dark" : "skeleton"}
                      ></div>
                    </td>
                    <td className="p-2"></td>
                    <td className="p-2">
                      <div
                        className={isDarkMode ? "skeleton-dark" : "skeleton"}
                      ></div>
                    </td>
                    <td className="p-2">
                      <div
                        className={isDarkMode ? "skeleton-dark" : "skeleton"}
                      ></div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;
